import type { AdminMedia, LocalizedPair, MediaUsage } from '@diora/contracts';
import { useQuery } from '@tanstack/react-query';
import { Image as ImageIcon } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { PageHeader } from '@/components/app-shell';
import { BilingualField } from '@/components/bilingual';
import { FormPage, RecordPage, useDirty, useRecord } from '@/components/form-page';
import { UploadButton } from '@/components/upload-button';
import { Alert, Badge, Button, Card, EmptyState, Pagination, SkeletonRows, Toolbar } from '@/components/ui';
import { apiGet, apiPatch, apiPost, useAdminList, useAdminMutation, useListParams } from '@/hooks/admin';
import { ApiError } from '@/lib/errors';
import { qk } from '@/lib/query';
import { useAuth } from '@/providers/auth';

const URL = '/admin/media';
/** The library this screen's detail page returns to. */
const LIST = '/media';
const INVALIDATE = [['media'], ['media-asset'], qk.dashboard];

export function MediaPage() {
  const { t } = useTranslation();
  const { can } = useAuth();
  const canEdit = can('OWNER', 'EDITOR');

  const [params, setParams] = useListParams();
  const list = useAdminList<AdminMedia>(qk.media(params), URL, params);

  return (
    <>
      <PageHeader
        title={t('media.title')}
        description={t('media.subtitle')}
        actions={
          canEdit && <UploadButton />
        }
      />

      <Toolbar>
        <p className="dio-hint">{t('media.dropHint')}</p>
      </Toolbar>

      {list.isLoading ? (
        <SkeletonRows />
      ) : list.rows.length === 0 ? (
        <EmptyState icon={<ImageIcon aria-hidden="true" />} title={t('media.empty')} hint={t('media.dropHint')} />
      ) : (
        <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {list.rows.map((media) => {
            const tooSmallFor = (['category', 'item', 'offer'] as const).filter(
              (surface) => !media.eligibleFor[surface],
            );
            return (
              <li key={media.id}>
                {/* A link, not a button: an asset has an address now, so this
                    can be opened in a second tab and shared with whoever has
                    to write its alt text. */}
                <Link to={`${LIST}/${media.id}`} className="dio-card block w-full text-start">
                  <img
                    src={media.urls.thumb ?? media.urls.medium ?? media.urls.original ?? ''}
                    alt={media.alt.ar || media.alt.en || media.originalName}
                    style={{ inlineSize: '100%', aspectRatio: '4 / 3', objectFit: 'cover' }}
                  />
                  <div className="dio-card__body">
                    <p className="truncate" dir="ltr">{media.originalName}</p>
                    {/* Dimensions and a byte size are measurements: RTL bidi
                        would reorder "640×480" into a different one. */}
                    <p className="dio-meta tabular" dir="ltr">
                      {media.width}×{media.height} · {Math.round(media.byteSize / 1024)} KB
                    </p>
                    {(!media.alt.ar || !media.alt.en) && (
                      <Badge tone="warning" dot>
                        {t('media.alt')} · {t('bilingual.missing')}
                      </Badge>
                    )}
                    {tooSmallFor.length > 0 && (
                      <p className="dio-meta mt-1">
                        {t('media.tooSmallFor', {
                          surfaces: tooSmallFor
                            .map((surface) => t(`media.for${surface[0]!.toUpperCase()}${surface.slice(1)}` as 'media.forItem'))
                            .join(' · '),
                        })}
                      </p>
                    )}
                  </div>
                </Link>
              </li>
            );
          })}
        </ul>
      )}

      <Pagination
        page={params.page ?? 1}
        pageSize={params.pageSize ?? 20}
        total={list.total}
        onPage={(page) => setParams({ page })}
      />

    </>
  );
}

export function MediaDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const { can } = useAuth();
  const canEdit = can('OWNER', 'EDITOR');

  const record = useRecord<AdminMedia>(qk.mediaAsset(id ?? ''), URL, id);

  return (
    <RecordPage title={t('media.title')} backTo={LIST} record={record}>
      {(row) => <MediaDetail key={row.id} media={row} canEdit={canEdit} />}
    </RecordPage>
  );
}

function MediaDetail({ media, canEdit }: { media: AdminMedia; canEdit: boolean }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [alt, setAlt] = useState<LocalizedPair>(media.alt);
  const [inUse, setInUse] = useState(false);

  const dirty = useDirty({ alt });

  const usage = useQuery({
    queryKey: qk.mediaUsage(media.id),
    queryFn: () => apiGet<MediaUsage>(`${URL}/${media.id}/usage`),
  });

  const save = useAdminMutation(
    () => apiPatch(`${URL}/${media.id}`, { expectedVersion: media.version, altAr: alt.ar, altEn: alt.en }),
    { successMessage: t('common.saved'), invalidate: INVALIDATE, onSuccess: () => navigate(LIST) },
  );

  const archive = useAdminMutation(
    () => apiPost(`${URL}/${media.id}/archive`, { expectedVersion: media.version }),
    {
      successMessage: t('publish.archived'),
      invalidate: INVALIDATE,
      onSuccess: () => navigate(LIST),
      onError: (error) => {
        // Archiving an image a published record points at is refused outright —
        // there is no confirm override, because the result would be a broken
        // photo on the live menu.
        if (error instanceof ApiError && error.code === 'MEDIA_IN_USE') {
          setInUse(true);
          return true;
        }
      },
    },
  );

  const used = usage.data?.data;

  return (
    <FormPage
      // The stored filename, not a translated title: it is what the person who
      // uploaded the file will recognise, and it is not prose.
      title={<span dir="ltr">{media.originalName}</span>}
      description={t('media.subtitle')}
      backTo={LIST}
      dirty={dirty}
      saving={save.isPending}
      onSave={canEdit ? () => save.mutate(undefined) : undefined}
      footerStart={
        canEdit && (
          <Button
            variant="ghost"
            loading={archive.isPending}
            style={{ marginInlineEnd: 'auto' }}
            onClick={() => archive.mutate(undefined)}
          >
            {t('publish.archive')}
          </Button>
        )
      }
    >
      <img
        src={media.urls.large ?? media.urls.medium ?? media.urls.original ?? ''}
        alt={media.alt.ar || media.alt.en || media.originalName}
        style={{ inlineSize: '100%', objectFit: 'contain', maxBlockSize: '18rem' }}
      />

      <p className="dio-meta">
        {t('media.dimensions')}:{' '}
        <span className="tabular" dir="ltr">
          {media.width}×{media.height}
        </span>{' '}
        · {t('media.fileSize')}:{' '}
        <span className="tabular" dir="ltr">
          {Math.round(media.byteSize / 1024)} KB
        </span>{' '}
        · <span dir="ltr">{media.mimeType}</span>
      </p>

      {inUse && <Alert tone="danger">{t('media.archiveInUse')}</Alert>}

      <BilingualField
        label={t('media.alt')}
        hint={t('media.altHint')}
        value={alt}
        onChange={setAlt}
        required
        maxLength={300}
        disabled={!canEdit}
      />

      <Card title={t('media.usage')}>
        {usage.isLoading ? (
          <SkeletonRows rows={2} />
        ) : !used || used.total === 0 ? (
          <p className="dio-meta">{t('media.usageEmpty')}</p>
        ) : (
          <ul className="flex flex-col gap-1">
            {used.categories.map((row) => (
              <li key={row.id}>{t('nav.categories')}: {row.name.ar || row.name.en}</li>
            ))}
            {used.items.map((row) => (
              <li key={row.id}>{t('nav.items')}: {row.name.ar || row.name.en}</li>
            ))}
            {used.offers.map((row) => (
              <li key={row.id}>{t('nav.offers')}: {row.title.ar || row.title.en}</li>
            ))}
            {used.branches.map((row) => (
              <li key={row.id}>{t('nav.branches')}: {row.name.ar || row.name.en}</li>
            ))}
          </ul>
        )}
      </Card>
    </FormPage>
  );
}
